package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.attribution.LinkCampaign
import com.steamstreet.vegasful.attribution.LinkMedium
import com.steamstreet.vegasful.attribution.LinkSource
import com.steamstreet.vegasful.attribution.LinkTags
import com.steamstreet.vegasful.attribution.taggedUrl
import com.steamstreet.vegasful.css.VegasfulStyles
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import kotlin.js.Date

/**
 * Mobile interstitial that shows app download links for mobile users.
 * Only displays once per 24 hours based on localStorage.
 */
fun mobileInterstitial() {
    // Check if the user is on a mobile device
    if (!isMobileDevice()) return

    // Check if we've shown the interstitial in the last 24 hours
    if (hasShownInterstitialRecently()) return

    // Create and show the interstitial
    createInterstitial()
}

/**
 * Detects if the user is on a mobile device by checking the user agent.
 */
private fun isMobileDevice(): Boolean {
    val userAgent = window.navigator.userAgent.lowercase()
    return userAgent.contains("android") ||
            userAgent.contains("iphone") ||
            userAgent.contains("ipad") ||
            userAgent.contains("ipod") ||
            userAgent.contains("mobile")
}

/**
 * Detects if the user is on an iOS device.
 */
private fun isIOSDevice(): Boolean {
    val userAgent = window.navigator.userAgent.lowercase()
    return userAgent.contains("iphone") ||
            userAgent.contains("ipad") ||
            userAgent.contains("ipod")
}

/**
 * Detects if the user is on an Android device.
 */
private fun isAndroidDevice(): Boolean {
    val userAgent = window.navigator.userAgent.lowercase()
    return userAgent.contains("android")
}

/**
 * The tags every install link from this interstitial carries. [platform] becomes `utm_content`, so
 * the two store buttons can be told apart when only one of them is ever shown to a given visitor.
 */
private fun installTags(platform: String) = LinkTags(
    source = LinkSource.WEB,
    medium = LinkMedium.APP_INSTALL,
    campaign = LinkCampaign.WEB_INTERSTITIAL,
    content = platform
)

/**
 * The Play Store listing, tagged so the install can be joined back to this interstitial.
 *
 * Play reads campaign information out of the `referrer` parameter rather than off the URL itself:
 * the value is surfaced verbatim to the app through the Install Referrer API on first launch, and
 * Play Console parses the UTMs inside it for its own acquisition reports. So the whole tag string
 * goes in there, encoded once as a parameter value.
 */
private fun playStoreLink(): String = taggedUrl(
    "https://play.google.com/store/apps/details?id=com.steamstreet.vegasful",
    listOf("referrer" to installTags("android").queryString())
)

/**
 * The App Store listing, tagged with Apple's campaign token.
 *
 * Apple takes a single opaque `ct` rather than UTMs, and pairs it with a `pt` provider token that
 * only exists once a campaign is created in App Store Connect. Until one is, `ct` alone is what
 * there is to send; it costs nothing and starts appearing the moment the provider side is set up.
 */
private fun appStoreLink(): String = taggedUrl(
    "https://apps.apple.com/us/app/vegasful/id6741783072",
    listOf("ct" to "${LinkCampaign.WEB_INTERSTITIAL}_ios")
)

/**
 * Checks if the interstitial has been shown in the last 24 hours.
 */
private fun hasShownInterstitialRecently(): Boolean {
    val lastShown = localStorage.getItem(INTERSTITIAL_LAST_SHOWN_KEY)
    if (lastShown != null) {
        val lastShownTime = lastShown.toDouble()
        val currentTime = Date.now()
        // Check if 24 hours (86400000 milliseconds) have passed
        if (currentTime - lastShownTime < 86400000) {
            return true
        }
    }
    return false
}

/**
 * Records that the interstitial has been shown.
 */
private fun recordInterstitialShown() {
    localStorage.setItem(INTERSTITIAL_LAST_SHOWN_KEY, Date.now().toString())
}

/**
 * Creates and displays the mobile interstitial.
 */
private fun createInterstitial() {
    val interstitialDiv = document.createElement("div") as HTMLDivElement
    interstitialDiv.id = "mobile-app-interstitial"
    interstitialDiv.style.apply {
        position = "fixed"
        top = "0"
        left = "0"
        width = "100%"
        height = "100%"
        backgroundColor = "rgba(0, 0, 0, 0.8)"
        zIndex = "9999"
        display = "flex"
        flexDirection = "column"
        justifyContent = "center"
        alignItems = "center"
        padding = "20px"
        boxSizing = "border-box"
    }

    val contentDiv = document.createElement("div") as HTMLDivElement
    contentDiv.style.apply {
        // Apply VegasfulStyles.popupWindow styles
        backgroundColor = VegasfulStyles.darkBlue.value // Override the default darkerPurple from popupWindow
        borderRadius = "10px" // Override the default 5px from popupWindow
        boxShadow = "0px 0px 15px 0px ${VegasfulStyles.purple.value}" // Apply boxShadow from VegasfulStyles.popupWindow
        padding = "20px"
        maxWidth = "400px"
        width = "90%"
        textAlign = "left"
    }

    // Header container with logo and title
    val headerContainer = document.createElement("div") as HTMLDivElement
    headerContainer.style.apply {
        display = "flex"
        alignItems = "center"
        marginBottom = "20px"
    }

    // Vegasful icon (left-aligned)
    val vegasfulIcon = document.createElement("img") as HTMLElement
    vegasfulIcon.setAttribute("src", "/assets/icons/apple-touch-icon.png")
    vegasfulIcon.setAttribute("alt", "Vegasful Logo")
    vegasfulIcon.style.apply {
        width = "80px"
        height = "80px"
        marginRight = "15px"
    }
    headerContainer.appendChild(vegasfulIcon)

    // Title container (to the right of the logo)
    val titleContainer = document.createElement("div") as HTMLDivElement

    // "Vegasful" text (bold)
    val appName = document.createElement("div") as HTMLElement
    appName.textContent = "Vegasful"
    appName.style.apply {
        fontWeight = "bold"
        fontSize = "24px"
        color = VegasfulStyles.white.value
        marginBottom = "5px"
    }
    titleContainer.appendChild(appName)

    // "Your Perfect Vegas" text
    val tagline = document.createElement("div") as HTMLElement
    tagline.textContent = "Your Perfect Vegas"
    tagline.style.apply {
        fontSize = "16px"
        color = VegasfulStyles.white.value
    }
    titleContainer.appendChild(tagline)

    headerContainer.appendChild(titleContainer)
    contentDiv.appendChild(headerContainer)

    // Bold heading for list
    val listHeading = document.createElement("div") as HTMLElement
    listHeading.textContent = "Gain access to the best of Vegas"
    listHeading.style.apply {
        fontWeight = "bold"
        color = VegasfulStyles.white.value
        marginBottom = "10px"
    }
    contentDiv.appendChild(listHeading)

    // Bullet points list
    val bulletList = document.createElement("ul") as HTMLElement
    bulletList.style.apply {
        color = VegasfulStyles.white.value
        marginBottom = "20px"
        paddingLeft = "20px"
        listStyleType = "disc" // Ensure bullets are displayed
    }

    // Create bullet points
    val bulletPoints = listOf(
        "Get personalized recommendations and notifications",
        "Find under-the-radar shows",
        "Plan unforgettable experiences"
    )

    bulletPoints.forEach { text ->
        val listItem = document.createElement("li") as HTMLElement
        listItem.textContent = text
        listItem.style.apply {
            marginBottom = "5px"
        }
        bulletList.appendChild(listItem)
    }

    contentDiv.appendChild(bulletList)

    // Determine which store buttons to show based on device type
    val isIOS = isIOSDevice()
    val isAndroid = isAndroidDevice()

    // App Store button - only show on iOS or when device type is uncertain
    if (isIOS || (!isAndroid && !isIOS)) {
        val appStoreButton = document.createElement("a") as HTMLElement
        appStoreButton.setAttribute("href", appStoreLink())
        appStoreButton.setAttribute("target", "_blank")
        appStoreButton.style.apply {
            display = "block"
            textAlign = "center"
            marginBottom = "15px"
        }

        // Create image element for App Store
        val appStoreImage = document.createElement("img") as HTMLElement
        appStoreImage.setAttribute("src", "/assets/icons/AppStoreDownload.svg")
        appStoreImage.setAttribute("alt", "Download on the App Store")
        appStoreImage.style.apply {
            width = "200px"
            height = "60px" // Fixed height to match Google Play image
            objectFit = "contain"
        }

        appStoreButton.appendChild(appStoreImage)
        contentDiv.appendChild(appStoreButton)
    }

    // Google Play button - only show on Android or when device type is uncertain
    if (isAndroid || (!isAndroid && !isIOS)) {
        val playStoreButton = document.createElement("a") as HTMLElement
        playStoreButton.setAttribute("href", playStoreLink())
        playStoreButton.setAttribute("target", "_blank")
        playStoreButton.style.apply {
            display = "block"
            textAlign = "center"
            marginBottom = "20px"
        }

        // Create image element for Google Play
        val playStoreImage = document.createElement("img") as HTMLElement
        playStoreImage.setAttribute("src", "/assets/icons/GooglePlayDownload.png")
        playStoreImage.setAttribute("alt", "Get it on Google Play")
        playStoreImage.style.apply {
            width = "200px"
            height = "60px" // Fixed height to match App Store image
            objectFit = "contain"
        }

        playStoreButton.appendChild(playStoreImage)
        contentDiv.appendChild(playStoreButton)
    }

    // Continue to website link
    val continueLink = document.createElement("a") as HTMLElement
    continueLink.textContent = "Continue to Website"
    continueLink.style.apply {
        display = "block"
        padding = "10px"
        textAlign = "center"
        cursor = "pointer"
        width = "100%"
        fontWeight = "bold"
        color = VegasfulStyles.pink.value
    }
    continueLink.onclick = {
        document.body?.removeChild(interstitialDiv)
        recordInterstitialShown()
        it.preventDefault()
    }
    continueLink.setAttribute("href", "#")
    contentDiv.appendChild(continueLink)

    interstitialDiv.appendChild(contentDiv)
    document.body?.appendChild(interstitialDiv)
}

// Key for storing the timestamp when the interstitial was last shown
private const val INTERSTITIAL_LAST_SHOWN_KEY = "vegasful_mobile_interstitial_last_shown"
